import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import {
  SitesService,
  STORED_SELECTED_SITE_KEY,
} from "./core/sites/sites.service";
import { PartnersService } from "./partner/global/partners.service";

export const commonEndpointsToIntercept = [
  "HelpAssistUsers",
  "helpAssistTickets",
  "PartnerSiteTicketUpdates",
];

const endpointsWithoutSiteHeaders = ["enterprise-sites", "enterprise-partners"];

@Injectable()
export class SiteHeaderInterceptor implements HttpInterceptor {
  public constructor(
    private readonly partners: PartnersService,
    private readonly sites: SitesService,
  ) {}

  public intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (
      endpointsWithoutSiteHeaders.some((endpoint) =>
        hasLastPathSegment(req, endpoint),
      )
    ) {
      return next.handle(req);
    }

    // If the request is to the "sites" endpoint, add site headers.
    // We can't use sites.selectedChanges here because it depends on the
    // response of this endpoint (chicken-and-egg), so we resolve the target
    // from the partner's site list instead.
    if (isSiteEndpoint(req)) {
      return this.partners.selectedChanges.pipe(
        take(1),
        switchMap((partner) => {
          if (!partner.sites.length) {
            return next.handle(req);
          }

          // Prefer the site the URL names, but a just-created site isn't in
          // the cached list yet, so still fall back instead of sending none.
          const routedSiteId = getRoutedSiteId(req);
          const storedSiteId = sessionStorage.getItem(STORED_SELECTED_SITE_KEY);
          const targetSite =
            (routedSiteId === null
              ? undefined
              : partner.sites.find((s) => s.id === routedSiteId)) ??
            partner.sites.find((s) => s.id.toString() === storedSiteId) ??
            partner.sites[0];

          if (!targetSite) {
            return next.handle(req);
          }

          const cloned = req.clone({
            setHeaders: {
              "site-id": targetSite.id.toString(),
              "master-site-id": targetSite.masterSiteId.toString(),
              "master-partner-id": partner.masterPartnerId.toString(),
            },
          });

          return next.handle(cloned);
        }),
      );
    }

    if (req.url.includes("/common")) {
      // intercept only specific common endpoints to add site headers
      if (
        commonEndpointsToIntercept.some((ep) => hasLastPathSegment(req, ep))
      ) {
        return this.withSiteHeaders(req, next);
      }

      // skip endpoints containing "/common" ,these endpoints should be sent without a site info headers
      // as they are either not site-specific or are used to get the core data to load the app
      return next.handle(req);
    }

    // For all other requests (partner-based), add site headers based on the selected site
    return this.withSiteHeaders(req, next);
  }

  private withSiteHeaders(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return combineLatest([
      // take(1) on each source to snapshot the current partner/site values
      // and prevent re-firing the request when the partner or site changes.
      this.partners.selectedChanges.pipe(take(1)),
      this.sites.selectedChanges.pipe(take(1)),
    ]).pipe(
      take(1),
      switchMap(([{ sites, masterPartnerId }, selectedSite]) => {
        // Stored SiteId in sessionStorage used to resolve the selected site.
        const storedSiteId = sessionStorage.getItem(STORED_SELECTED_SITE_KEY);

        // If no siteId is found in sessionStorage, we fall back to the selected siteId; if none of them return any matches, we fall back to the first site.
        const site =
          sites?.find(
            (s) =>
              s.id.toString() === (storedSiteId ?? selectedSite?.id.toString()),
          ) ?? sites[0];

        if (!site) {
          return next.handle(req);
        }

        const cloned = req.clone({
          setHeaders: {
            "site-id": site.id.toString(),
            "master-site-id": site.masterSiteId.toString(),
            "master-partner-id": masterPartnerId.toString(),
          },
        });

        return next.handle(cloned);
      }),
    );
  }
}

/**
 * Gets the lowercased URL path segments. API routes are case-insensitive and
 * both casings are used (e.g. OData `/sites` vs. API v2 `/Sites`).
 */
function getPathSegments(req: HttpRequest<unknown>): string[] {
  let path: string;
  try {
    path = new URL(req.url, window.location.origin).pathname;
  } catch {
    path = req.url.split("?")[0];
  }
  return path.toLowerCase().split("/").filter(Boolean);
}

function hasLastPathSegment(
  req: HttpRequest<unknown>,
  endpoint: string,
): boolean {
  const segments = getPathSegments(req);
  return (
    segments.length > 0 &&
    segments[segments.length - 1] === endpoint.toLowerCase()
  );
}

/**
 * Matches the sites list endpoint (/sites) and individual site resource
 * endpoints (/sites/308 and /Sites/308), but NOT deeper nested resources under
 * a site (e.g. /sites/308/appointmentDocks).
 */
function isSiteEndpoint(req: HttpRequest<unknown>): boolean {
  const segments = getPathSegments(req);
  return (
    segments[segments.length - 1] === "sites" || getRoutedSiteId(req) !== null
  );
}

/** The site ID addressed by an individual site URL, in either API casing. */
function getRoutedSiteId(req: HttpRequest<unknown>): number | null {
  const segments = getPathSegments(req);
  const last = segments[segments.length - 1] ?? "";
  return (segments[segments.length - 2] ?? "") === "sites" && /^\d+$/.test(last)
    ? Number(last)
    : null;
}
